import * as THREE from 'three';
import SpriteText from 'three-spritetext';
import Experience from '../Experience';
import Element from './Element';
import Plane from './Plane';
import LineElement from './LineElement';
import { 
    primaryColor, 
    secondaryColor, 
    lineDark,
    lineLight,
    white,
    brown,
    activeLineW,
    defaultLineW,
    duskBG,
    dayBG,
    dawnBG,
    nightBG,
    walla
 } from '../Utils/styles'
import { billboardEls, linesToBillboardEls, existingChannelIDs, upcomingConnections, interactedObjects } from './sets';
import ConnectionManager from '../Utils/ConnectionManager';
import LinePath from './LinePath';
import type { AnyObject, ExperienceLike } from '../types/core';
import type {
    LandingSpriteData,
    PathSpriteData,
    RegenChannelData,
    RegenTextData,
    SpriteElementInput,
    WalkthroughSpriteData,
} from '../types/domain';
import type { ArenaBlock } from '../types/arena';
import type { UserDataObject } from '../types/scene';
import { debugLog } from '../Utils/debugLog';

type SpriteLike = THREE.Object3D & {
    userData: UserDataObject;
    scale: THREE.Vector3;
    _canvas?: { attributes?: { width?: { value?: string | number }; height?: { value?: string | number } } };
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    borderWidth?: number;
    padding?: number;
    fontFace?: string;
    fontSize?: number;
};
type ConnectionCountData = {
    length?: number;
    counts?: { blocks?: number; contents?: number; connections?: number };
};
export default class SpriteElement {
    experience!: ExperienceLike
    scene!: THREE.Scene
    API!: ExperienceLike["API"]
    camera!: THREE.Camera
    textureLoader!: THREE.TextureLoader
    data: SpriteElementInput = {} as SpriteElementInput
    connectionManager!: ConnectionManager
    sizes: { width: number; height: number; pixelRatio?: number } = { width: 0, height: 0 }
    root: THREE.Object3D | null = null
    label: SpriteLike | null = null

    /** @param {SpriteElementInput} data */
    constructor(data: SpriteElementInput)
    {
        this.experience = new Experience()
        this.scene = this.experience.scene;
        this.API = this.experience.API;
        this.camera = this.experience.camera.instance as THREE.Camera;
        this.textureLoader =
            this.experience.resources.loaders?.textureLoader ?? new THREE.TextureLoader();
        this.data = data;
        this.connectionManager = this.experience.connectionManager as ConnectionManager
        this.sizes = this.experience.sizes
        this.create()
    }
    async create()
    {   const spriteData = this.data as AnyObject;
        const baseClass = spriteData.base_class || spriteData.base_type;
        const itemType = spriteData.type;
        if(spriteData.name === "Channel"){
            await this.regenChannel(this.data)
        } else  
        if(spriteData.name === "TextBlock"){
            await this.regenTextBlock(this.data)
        } 
        else if(baseClass === "Block"){
           await this.makeTextBlock(this.data)
        } else if(baseClass === "Channel" || itemType === "Channel"){
            this.makeConnectionLabel(this.data)
        } else if(this.isLandingSpriteData(this.data)){
            this.makeLandingLabel(this.data)
        } else if(this.isPathSpriteData(this.data)){
            this.makePathLabel(this.data)
        } else if(this.isWalkthroughSpriteData(this.data)){
            this.makeWalkthroughLabel(this.data)
        } else {
            console.error("No correct data found to create sprite", this.data)
        }
    }
    /** @param {SpriteElementInput} data @param {string} [bgColor] @param {string} [color] @param {number} [textHeight] */
    async regenTextBlock(data: SpriteElementInput, bgColor = white, color = brown, textHeight=60){
        if (!this.isRegenTextData(data)) return { root: null, label: null };
        const textData = data;
        const textInput = textData.content;
        const textContent = this.checkLineBreak(textInput, 10);
        const label = new SpriteText(textContent, textHeight, color);
        label.backgroundColor = bgColor
        label.fontSize = 60;
        label.fontFace = walla;
        label.borderColor = bgColor;
        label.borderWidth = 2;
        label.padding = 5;
        label.userData.direction = textData.direction
        label.userData.position = textData.position
        label.userData.active = textData.active
        label.userData.contentRendered = textData.contentRendered
        label.userData.blockType = "Text";
        label.userData.blockID = textData.blockID
        label.userData.connections = textData.connections
        label.userData.creationDate = textData.creationDate
        label.userData.generatedTitle = textData.generatedTitle
        label.userData.title = textData.title
        label.userData.source = textData.source
        label.userData.user = textData.user
        label.userData.instance = this.getLimitedInterface();
        if(!textData.spritescale){
            this.setScaleLabel(label)
        }
        // this.setLabelData(label, data)
        // this.connectionManager.setBlockData(label, data)
        const root = new THREE.Object3D();
        root.position.set(textData.position.x, textData.position.y, textData.position.z)
        root.userData.direction = textData.direction
        label.userData.positionRoot = root.position

        root.add(label);
        this.scene.add(root);
        await this.experience.sceneUtils.createDataLabels(label)

        label.name = "TextBlock"
        root.name = "TextBlock"
        this.root = root;
        this.label = label;
        return { root, label};
    }
    /** @param {SpriteElementInput} data @param {string} [bgColor] @param {string} [color] @param {number} [textHeight] */
    async regenChannel(data: SpriteElementInput, bgColor = brown, color = white, textHeight=60){
        if (!this.isRegenChannelData(data)) return { root: null, label: null };
        const channelData = data;
        debugLog("data regen channel", data);
        let textInput
        if(!channelData.channelData && channelData.connection){
            textInput = `${channelData.connection.title} \u273F ${channelData.connection.length}`
        } else {
            textInput = `${channelData.channelData?.title ?? ""} \u273F ${channelData.channelData?.length ?? 0}`
        }
        
        const label = new SpriteText(textInput, textHeight, color);
        label.backgroundColor = bgColor
        label.padding = 5;
        label.fontFace = walla;
        label.userData.direction = channelData.direction
        label.userData.position = channelData.position
        label.userData.regen = true
        label.userData.active = false
        label.userData.contentRendered = false
        // label.userData.active = data.active
        // label.userData.contentRendered = data.contentRendered
        label.userData.blockType = "Channel";
        label.userData.connection = channelData.connection
        label.userData.channelData = channelData.channelData
        label.userData.instance = this.getLimitedInterface();
        // this.setScaleLabel(label, data)
        // this.setLabelData(label, data)
        const root = new THREE.Object3D();
        root.position.set(channelData.position.x, channelData.position.y, channelData.position.z)
        label.userData.positionRoot = root.position
        root.userData.direction = channelData.direction
        root.add(label);
        this.scene.add(root);
        if (typeof channelData.spritescale === 'object' && channelData.spritescale !== null) {
            const { x, y, z } = channelData.spritescale;
    
            // Additional validation and logging
            if (typeof x === 'number' && typeof y === 'number' && typeof z === 'number') {
                if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
                    // console.log(`Setting scale to: x=${x}, y=${y}, z=${z}`);
                    const scale = new THREE.Vector3(x, y, z);
                    label.scale.set(scale.x, scale.y, scale.z);
                } else {
                    console.error(`Invalid scale values: x=${x}, y=${y}, z=${z}`);
                    // Set to default scale if invalid values are found
                    label.scale.set(1, 1, 1);
                }
            } else {
                console.error(`Invalid types for scale values: x=${x}, y=${y}, z=${z}`);
                label.scale.set(1, 1, 1);
            }
        } else {
            console.error('spritescale is not a valid object:', channelData.spritescale);
            label.scale.set(1, 1, 1);
        }
        label.name = "Channel"
        root.name = "Channel"
        this.root = root;
        this.label = label;
        interactedObjects.push(this.label)
        interactedObjects.push(this.root)
        return { root, label};

    }
    /** @param {SpriteElementInput} data */
    makePathLabel(data: SpriteElementInput)
    {
        if (!this.isPathSpriteData(data)) return { label: null };
        const pathData = data;
        // const pathLine = new LinePath(label, data)
        const textInput = pathData.name
        const label = new SpriteText(textInput, 1, white);
        label.fontFace = walla
        label.backgroundColor = brown
        label.fontSize = 100
        label.padding = 3;
        label.userData.data = pathData
        label.userData.instance = this.getLimitedInterface()
        label.name = `PathwayItemSprite`
        const parent = pathData.parent
        parent.add(label);
        this.applyCanvasScale(label, 0.001);
        // label.position.y = -.75
        this.label = label
        return { label};
    }
    /** @param {SpriteElementInput} data */
    makeLandingLabel(data: SpriteElementInput){
        if (!this.isLandingSpriteData(data)) return { label: null };
        const landingData = data;
        const textInput = landingData.title
        const label = new SpriteText(textInput, 1, white);
        if(landingData.slug){
            const anyLabel = label as SpriteLike & { slug?: string };
            anyLabel.slug = landingData.slug
        }
        label.fontFace = walla
        label.backgroundColor = brown
        label.fontSize = 100
        label.padding = 3;
        label.userData.data = landingData
        label.userData.instance = this.getLimitedInterface()
        label.name = `${textInput}Sprite`;
        this.scene.add(label);
        this.applyCanvasScale(label, 0.001);
        this.label = label
        return { label};
    }
    /** @param {SpriteElementInput} data */
    makeWalkthroughLabel(data: SpriteElementInput){
        if (!this.isWalkthroughSpriteData(data)) return { label: null };
        const walkthroughData = data;
        const textInput = walkthroughData.title
        // Create canvas element
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
            return { label: null };
        }
        const padding = 60;
        const maxWidth = 2500;
        const size = 125;
        // const pixelRatio = window.devicePixelRatio || 1; // Get device pixel ratio
        const color = white
        const bgColor = brown
        // Set font and size
        const font = `${size}px ${walla}`;
        
        // Calculate wrapped text lines
        const wrappedText = this.wrapTextBlockContent(context, textInput, font, maxWidth - 2 * padding);

        // Calculate canvas dimensions based on wrapped text and padding
        const lineHeight = size; // Line height with padding
        const textHeight = lineHeight * wrappedText.lines.length;
        const canvasWidth = maxWidth;
        const canvasHeight = textHeight + 2 * padding;

        // Set canvas dimensions
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        
        // context.scale(this.sizes.pixelRatio, this.sizes.pixelRatio);

        // Draw background
        context.fillStyle = bgColor; // Background color
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Draw wrapped text
        context.font = font;
        context.fillStyle = color; // Text color
        wrappedText.lines.forEach((line, index) => {
            const textX = padding; // Set x position to start from padding
            const textY = padding + (index + 1) * lineHeight - size * 0.2; // Calculate y position with padding
            context.fillText(line, textX, textY);
        });
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        const labelMaterial = new THREE.SpriteMaterial({ 
            map: texture, 
            transparent: true,
        });

        const label = new THREE.Sprite(labelMaterial);
        const labelBaseScale = 0.001;
        label.scale.x = canvas.width * labelBaseScale;
        label.scale.y = canvas.height * labelBaseScale;

        label.userData.data = walkthroughData;
        label.userData.instance = this.getLimitedInterface();
        label.name = `${textInput}Sprite`;
        const anyLabel = label as SpriteLike & { slug?: string };
        anyLabel.slug = walkthroughData.slug;

        this.label = label
        // console.log(label);
        this.scene.add(label);
        
        return { label };
        
        // const textInput = data.title
        // const label = new SpriteText(textInput, 1, white);
        // if(data.slug){
        //     label.slug = data.slug
        // }
        // label.fontFace = walla
        // label.backgroundColor = brown
        // label.fontSize = 100
        // label.padding = 3;
        // label.userData.data = data
        // label.userData.instance = this.getLimitedInterface()
        // label.name = `${textInput}Sprite`
        // this.scene.add(label);
        // label.scale.x = label._canvas.attributes.width.value * (0.001);
        // label.scale.y = label._canvas.attributes.height.value * (0.001);
        // this.label = label
        // return { label};
    }
    wrapTextBlockContent = (context: CanvasRenderingContext2D, text: unknown, font: string, maxWidth=500) => {
        /** @type {string[]} */
        const lines: string[] = [];
        const normalizedText = typeof text === "string" ? text : String(text ?? "");
        const paragraphs = normalizedText.split('\n');
        
        context.font = font;
        paragraphs.forEach((paragraph: string) => {
            const words = paragraph.split(' ');
            let currentLine = '';
            words.forEach((word: string) => {
                const testLine = currentLine + (currentLine ? ' ' : '') + word;
                const testWidth = context.measureText(testLine).width;
                if (testWidth > maxWidth) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = testLine;
                }
            });
            lines.push(currentLine);
        })
        return { lines };
      }  
    async onClick() 
    {
        if (!this.label) return;
        if(this.label.userData.blockType === "Text"){
            if (this.label.userData.linesCreated) {
                // snackBarMessage("Connections already exist");
            } else {
                await this.connectionManager.fetchConnections(this.data, this.label)
                // this.experience.plane.fetchConnections();
                this.label.userData.linesCreated = true;
            }
        } else if (this.label.userData.blockType === "Channel"){
            this.experience.sceneOperations.setSceneFirstName(this.label)
            this.changeSpriteColor(this.label, primaryColor, brown)
            this.label.userData.active = true;
            if (this.label.userData.contentRendered) {
                // snackBarMessage("Content already rendered");
            } else {
                const hasChannelContents = Array.isArray(this.label.userData.channelData?.contents);
                if (!this.label.userData.channelData || !hasChannelContents) {
                    try {
                        const fetchedChannel = await this.connectionManager.fetchChannel(this.label, this.label.userData);
                        if (!fetchedChannel) {
                            return;
                        }
                        const { data, error } = fetchedChannel;
                        if (error) {
                            console.error("Error fetching channel:", error);
                            return;
                        }
                        this.label.userData.channelData = data;
                        this.experience.sceneOperations.setSceneLastName(this.label);
                        this.label.userData.position = this.label.position.clone();
                        this.connectionManager.renderChannelItems(this.label.userData.channelData, this.label.userData);
                        this.label.userData.contentRendered = true;
                    } catch (error) {
                        console.error("Error rendering channel items:", error);
                        return;
                    }
                } else {
                    this.label.userData.position = this.label.position.clone();
                    this.experience.sceneOperations.setSceneLastName(this.label);
                    await this.connectionManager.renderChannelItems(this.label.userData.channelData, this.label.userData);
                    this.label.userData.contentRendered = true;
                }
            }
        }
    }
    onHover() 
    {
        // console.log("Sprite hovered!", this);
    }
    /** @param {SpriteElementInput} data @param {string} [bgColor] @param {string} [color] @param {number} [textHeight] */
    async makeTextBlock(data: SpriteElementInput, bgColor = white, color= brown, textHeight = 60)
    {
        if (!this.isRegenTextData(data)) return { root: null, label: null };
        const textData = data;
        const textInput = textData.content;
        const textContent = this.checkLineBreak(textInput, 10);
        const label = new SpriteText(textContent, textHeight, color);
        label.backgroundColor = bgColor
        label.fontSize = 60;
        label.fontFace = walla;
        label.borderColor = bgColor;
        label.borderWidth = 2;
        label.padding = 5;
        label.userData.instance = this.getLimitedInterface()
        // this.setLabelData(label, data)
        if (this.isArenaBlock(data)) {
            this.connectionManager.setBlockData(label, data)
        }
        this.setScaleLabel(label)
        const root = new THREE.Object3D();
        root.add(label);
        this.scene.add(root);
        await this.experience.sceneUtils.createDataLabels(label)
        label.name = "TextBlock"
        root.name = "TextBlock"
        this.root = root;
        this.label = label;
        return { root, label};
    }
    /** @param {SpriteElementInput} data @param {string} [bgColor] @param {string} [color] @param {number} [textHeight] */
    makeConnectionLabel(data: SpriteElementInput, bgColor = brown, color = white, textHeight=60)
    {
        // console.log(this.data);
        const connectionCount = this.getConnectionCount(data);
        const spriteData = this.data as AnyObject;
        const labelTitle = spriteData.title || spriteData.slug || "Untitled channel";
        const textInput = `${labelTitle} \u273F ${connectionCount}`
        const label = new SpriteText(textInput, textHeight, color);
        label.backgroundColor = bgColor
        label.padding = 5;
        label.fontFace = walla;
        this.setScaleLabel(label, data)
        this.setLabelData(label, data)
        const root = new THREE.Object3D();
        root.add(label);
        this.scene.add(root);
        label.name = "Channel"
        root.name = "Channel"
        this.root = root;
        this.label = label;


        return { root, label};
    }
    /** @param {{ length: number }} data */
    getScaleLabelFromData(data: { length: number })
    {
        let labelBaseScale;
        let connectionFactor;
        if(data.length <= 1000){
            labelBaseScale = 0.00005
            connectionFactor = Math.sqrt(data.length); 
        } else {
            labelBaseScale = 0.0003
            connectionFactor = Math.log(data.length); 
        }
        return {labelBaseScale, connectionFactor}
    }
    /** @param {SpriteLike} label @param {SpriteElementInput} [data] */
    setScaleLabel(label: SpriteLike, data?: SpriteElementInput)
    {
        const scaleData = data as unknown as { id?: number | string } | undefined;
        const connectionCount = this.getConnectionCount(data);
        const canvasWidth = Number(label._canvas?.attributes?.width?.value || 0);
        const canvasHeight = Number(label._canvas?.attributes?.height?.value || 0);
        if(!scaleData || connectionCount < 20 || scaleData.id === 28647202 || scaleData.id === 2923323 || scaleData.id === 2923335 || scaleData.id === 2923364 || scaleData.id === 2923359 || scaleData.id === 2923365){
            label.scale.x = canvasWidth * (0.001);
            label.scale.y = canvasHeight * (0.001);
        } else {
            const {labelBaseScale, connectionFactor} = this.getScaleLabelFromData({ ...scaleData, length: connectionCount })
            label.scale.x = canvasWidth * (labelBaseScale*connectionFactor);
            label.scale.y = canvasHeight * (labelBaseScale*connectionFactor);
        }
    }
    /** @param {SpriteLike} spriteLabel @param {number} factor */
    applyCanvasScale(spriteLabel: SpriteLike, factor: number)
    {
        const canvasWidth = Number(spriteLabel._canvas?.attributes?.width?.value || 0);
        const canvasHeight = Number(spriteLabel._canvas?.attributes?.height?.value || 0);
        spriteLabel.scale.x = canvasWidth * factor;
        spriteLabel.scale.y = canvasHeight * factor;
    }
    /** @param {unknown} data */
    getConnectionCount(data: unknown)
    {
        const countData = data as ConnectionCountData | undefined;
        const rawCount =
            countData?.length ??
            countData?.counts?.blocks ??
            countData?.counts?.contents ??
            countData?.counts?.connections ??
            0;
        const parsedCount = Number(rawCount);
        return Number.isFinite(parsedCount) ? parsedCount : 0;
    }
    /** @param {SpriteLike} label @param {unknown} data */
    setLabelData(label: SpriteLike, data: ConnectionCountData | unknown)
    {
        label.userData.contentRendered = false;
        label.userData.blockType = "Channel";
        label.userData.connection = data
        label.userData.instance = this.getLimitedInterface();
        // label.userData.creationDate = new Date(data.created_at).toLocaleDateString();
    }
    
    /** @param {SpriteLike} sprite @param {string} bgColor @param {string} [color] @param {string} [borderColor] */
    changeSpriteColor(sprite: SpriteLike, bgColor: string, color = brown, borderColor = bgColor)
    {
        const scale = sprite.scale.clone();
        sprite.backgroundColor = bgColor;
        sprite.color = color;
        if(sprite.userData && sprite.userData.blockType === "Text"){
            sprite.borderColor = borderColor;
            sprite.borderWidth = 2;
        }
        sprite.scale.set(scale.x, scale.y, scale.z);
    }
    /** @param {unknown} text @param {number} [words] @param {string} [symbol] */
    checkLineBreak(text: unknown, words?: number, symbol?: string): string
    {
        const normalizedText =
            typeof text === "string"
                ? text
                : (text as { plain?: string; markdown?: string })?.plain ||
                  (text as { plain?: string; markdown?: string })?.markdown ||
                  String(text ?? "");
        const resultWords = normalizedText.split(" ");
        if (!words) {
            words = 6;
        }
        if (!symbol) {
            symbol = "\n";
        }
        if (resultWords.length > words) {
            for (let i = 1; i < resultWords.length; i++) {
                if (i % words == 0) {
                    resultWords.splice(i, 0, symbol);
                } else {
                    continue;
                }
            }
        }
        return resultWords.join(" ");
    }
    
    getLimitedInterface() {
        return {
            changeSpriteColor: this.changeSpriteColor.bind(this),
            onHover: this.onHover.bind(this),
            onClick: this.onClick.bind(this),
        };
    }
    private isRegenTextData(data: SpriteElementInput): data is RegenTextData {
        return Boolean(data && typeof data === "object" && "position" in data);
    }
    private isRegenChannelData(data: SpriteElementInput): data is RegenChannelData {
        return Boolean(
            data &&
            typeof data === "object" &&
            "position" in data &&
            ("channelData" in data || "connection" in data)
        );
    }
    private isPathSpriteData(data: SpriteElementInput): data is PathSpriteData {
        return Boolean(data && typeof data === "object" && "role" in data && data.role === "Path" && "parent" in data);
    }
    private isLandingSpriteData(data: SpriteElementInput): data is LandingSpriteData {
        return Boolean(data && typeof data === "object" && "role" in data && data.role === "Landing");
    }
    private isWalkthroughSpriteData(data: SpriteElementInput): data is WalkthroughSpriteData {
        return Boolean(data && typeof data === "object" && "role" in data && data.role === "Walkthrough");
    }
    private isArenaBlock(data: SpriteElementInput): data is ArenaBlock {
        return Boolean(data && typeof data === "object" && "id" in data && "type" in data);
    }
}
